import { Select } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { COUNTRY_CODES, countryName, countryOptions } from "../api/countries";

/**
 * Marks the end of the countries offered first. country_select drew the same
 * line in the old application, and it answers the question a plain list raises:
 * why Germany stands above Albania.
 */
const SEPARATOR = "__separator__";

/**
 * The country of an address, stored as its ISO 3166-1 alpha-2 code — what the
 * old application wrote and what half the rows in the database already hold.
 *
 * It replaces a free text box, which is why it tolerates what that box left
 * behind: a value that is not a code ("Schweiz", typed by hand years ago) is
 * kept as an option of its own instead of being silently dropped from a form
 * the user then saves. Picking a country from the list replaces it.
 *
 * Written for Form.Item, which supplies value and onChange.
 */
export function CountrySelect({
  value,
  onChange,
  style,
}: {
  value?: string;
  onChange?: (value: string) => void;
  style?: React.CSSProperties;
}) {
  const { t, i18n } = useTranslation();

  const options = useMemo(() => {
    const list: { value: string; label: string; disabled?: boolean }[] = [];

    // Whatever is stored but is not a country code, so it stays readable and
    // is not lost by opening the form.
    if (value && !COUNTRY_CODES.includes(value)) {
      list.push({ value, label: value });
    }

    const all = countryOptions(i18n.language);
    list.push(...all.slice(0, 6));
    list.push({ value: SEPARATOR, label: "──────────", disabled: true });
    list.push(...all.slice(6));

    return list;
  }, [value, i18n.language]);

  return (
    <Select
      showSearch
      allowClear
      style={style}
      value={value || undefined}
      // Clearing sends "" and not undefined: the forms hand their values
      // straight to the API, and a key that is missing from the body leaves the
      // stored country as it was instead of emptying it.
      onChange={(next) => onChange?.(next ?? "")}
      options={options}
      placeholder={t("helpers.select.prompt")}
      // Typed text is matched against the name and against the code: someone
      // who knows "IT" should not have to spell out "Italien".
      filterOption={(input, option) => {
        if (!option || option.disabled) return false;

        const needle = input.trim().toLowerCase();
        return (
          option.label.toLowerCase().includes(needle) ||
          option.value.toLowerCase().startsWith(needle)
        );
      }}
    />
  );
}

/** The name behind a stored code, for the screens that only display one. */
export { countryName };
